import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Divider,
  TextField,
  Grid,
  Button,
  Tooltip,
} from "@mui/material";

import {
  GradingDetailsDTO,
  GradingDetailsGroupDTO,
  ParticipantGradingDTO,
  ParticipantGradingRemarkDTO,
} from "../types/ExerciseGrading";
import { MergeModeEnum } from "../enums/MergeModeEnum";
import { TeamMemberDTO } from "../types/teamMembers";
import { useLocation } from "react-router-dom";
import {
  FaCheck,
  FaCheckCircle,
  FaExclamationCircle,
  FaBullseye,
  FaCommentDots,
} from "react-icons/fa";

const FONT_STYLE = { fontFamily: "Verdana", fontSize: "13px" };

const statusConfig = {
  Complete: {
    icon: FaCheckCircle,
    color: "#2e7d32",
  },
  Incomplete: {
    icon: FaBullseye,
    color: "#ed6c02",
  },
  Conflict: {
    icon: FaExclamationCircle,
    color: "#d32f2f",
  },
};

interface Props {
  groups: GradingDetailsGroupDTO[];
  results: ParticipantGradingDTO[];
  allRemarks: ParticipantGradingRemarkDTO[];
  onChange: React.Dispatch<React.SetStateAction<ParticipantGradingDTO[]>>;
  handleSendResults: (groupId: number) => Promise<void>;
  handleSaveRemarks: (groupId: number, remarks: string) => Promise<void>;
}

const MergePanel: React.FC<Props> = ({
  groups,
  results,
  allRemarks,
  onChange,
  handleSendResults,
  handleSaveRemarks,
}) => {
  const [editingKey, setEditingKey] = React.useState<string | null>(null);
  const [editValue, setEditValue] = React.useState("");
  const [modifiedGroups, setModifiedGroups] = React.useState<Set<number>>(
    new Set(),
  );
  const [savedGroups, setSavedGroups] = React.useState<Set<number>>(new Set());
  const [remarksByGroup, setRemarksByGroup] = React.useState<
    Record<number, string>
  >({});
  const [initialRemarksByGroup, setInitialRemarksByGroup] = React.useState<
    Record<number, string>
  >({});
  const remarksInitialised = React.useRef(false);

  const location = useLocation();

  React.useEffect(() => {
    if (remarksInitialised.current || allRemarks.length === 0) return;
    remarksInitialised.current = true;
    const mapped: Record<number, string> = {};
    allRemarks
      .filter((r) => r.teamMemberID === null || r.teamMemberID === undefined)
      .forEach((r) => {
        mapped[r.gradingDetailsGroupID] = r.remark ?? "";
      });
    setRemarksByGroup(mapped);
    setInitialRemarksByGroup(mapped);
  }, [allRemarks]);

  type ParticipantGradingUpdate = Partial<
    Omit<
      ParticipantGradingDTO,
      "gradingDetailsID" | "gradingDetailsGroupID" | "participantID"
    >
  >;

  function updateMemberResult(
    gradingDetailsID: number,
    memberId: number,
    updates: ParticipantGradingUpdate,
    groupId: number,
  ) {
    onChange((prev) =>
      prev.map((r) =>
        r.gradingDetailsID === gradingDetailsID && r.teamMember?.id === memberId
          ? { ...r, ...updates, hasGradingByTeamMember: true }
          : r,
      ),
    );
    setModifiedGroups((prev) => new Set(prev).add(groupId));
  }

  const mergeMode = useMemo<MergeModeEnum>(() => {
    const merge = Number(new URLSearchParams(location.search).get("merge"));
    return merge in MergeModeEnum ? merge : MergeModeEnum.Grading;
  }, [location.search]);

  const maxPts = useMemo(() => {
    const m: Record<number, number> = {};
    groups.forEach((g) =>
      g.gradingDetails.forEach((d) => (m[d.id] = d.points)),
    );
    return m;
  }, [groups]);

  const members = useMemo(() => {
    const map = new Map<number, ParticipantGradingDTO["teamMember"]>();
    results.forEach((r) => {
      if (r.teamMember) map.set(r.teamMember.id, r.teamMember);
    });
    return Array.from(map.values());
  }, [results]);

  const byDetail = useMemo(() => {
    const map: Record<number, ParticipantGradingDTO[]> = {};
    results.forEach((r) => {
      map[r.gradingDetailsID] ??= [];
      map[r.gradingDetailsID].push(r);
    });
    return map;
  }, [results]);

  function getStatus(
    memberResults: ParticipantGradingDTO[],
    totalMembers: number,
  ) {
    if (
      memberResults.length < totalMembers ||
      memberResults.find((mr) => !mr.hasGradingByTeamMember)
    )
      return "Incomplete";

    if (mergeMode !== MergeModeEnum.MergeResult) {
      const firstPoints = memberResults[0].points;
      if (memberResults.some((r) => r.points !== firstPoints)) {
        return "Conflict";
      }
    }

    return "Complete";
  }

  const hasGroupConflict = (group: GradingDetailsGroupDTO) => {
    return group.gradingDetails.some((d) => {
      const all = byDetail[d.id] ?? [];
      const memberResults = all.filter((r) => r.teamMember);
      const status = getStatus(memberResults, members.length);
      return status !== "Complete";
    });
  };

  const totalsByGroup = useMemo(() => {
    return groups.map((g) => {
      const base = {
        merged: 0,
        members: members.map(() => 0),
      };

      g.gradingDetails.forEach((d) => {
        const all = byDetail[d.id] ?? [];
        const memberResults = all.filter((r) => r.teamMember);
        const merged = all.find((r) => !r.teamMember);
        const status = getStatus(memberResults, members.length);

        let mergeValue = 0;
        if (status === "Complete" && mergeMode === MergeModeEnum.Merge) {
          const firstMemberResult = memberResults.find(
            (x) => x.teamMember?.id === members[0]?.id,
          );
          mergeValue = firstMemberResult?.points ?? 0;
        } else {
          mergeValue = merged?.points ?? 0;
        }
        base.merged += mergeValue;

        all.forEach((r) => {
          if (r.teamMember) {
            const idx = members.findIndex((m) => m?.id === r.teamMember?.id);
            if (idx >= 0) base.members[idx] += r.points;
          }
        });
      });

      return {
        merged: base.merged * g.averageFactor,
        members: base.members.map((v) => v * g.averageFactor),
      };
    });
  }, [groups, byDetail, members, mergeMode, getStatus]);

  const commitEdit = (
    r: ParticipantGradingDTO,
    d: GradingDetailsDTO,
    m: TeamMemberDTO,
    groupId: number,
  ) => {
    if (!r) return;

    const raw = parseFloat(editValue.replace(",", "."));
    const value = !isNaN(raw) && raw >= 0 ? Math.min(raw, maxPts[d.id]) : 0;

    updateMemberResult(d.id, m!.id, { points: value, checked: true }, groupId);
    setEditingKey(null);
  };

  const hasRemarkChanged = (groupId: number): boolean =>
    (remarksByGroup[groupId] ?? "") !== (initialRemarksByGroup[groupId] ?? "");

  const handleSaveGroup = async (groupId: number) => {
    await handleSendResults(groupId);
    await handleSaveRemarks(groupId, remarksByGroup[groupId] ?? "");

    setModifiedGroups((prev) => {
      const next = new Set(prev);
      next.delete(groupId);
      return next;
    });
    setSavedGroups((prev) => new Set(prev).add(groupId));
    setInitialRemarksByGroup((prev) => ({
      ...prev,
      [groupId]: remarksByGroup[groupId] ?? "",
    }));
  };

  const needsMerge = (group: GradingDetailsGroupDTO) => {
    const hasMergeResult = results.find(
      (r) => r.teamMember === null && r.gradingDetailsGroupID === group.id,
    );

    if (hasMergeResult) return false;

    return group.gradingDetails.every((d) => {
      const all = byDetail[d.id] ?? [];
      const memberResults = all.filter((r) => r.teamMember);
      const status = getStatus(memberResults, members.length);
      return status === "Complete";
    });
  };

  return (
    <Box sx={{ overflowY: "auto", pr: 2, mt: 2 }}>
      {groups
        .slice()
        .sort((a, b) => a.index - b.index)
        .map((group, gi) => {
          const conflict = hasGroupConflict(group);
          const hasMergeResult = results.find(
            (r) =>
              r.teamMember === null && r.gradingDetailsGroupID === group.id,
          );
          const hasBeenModified = modifiedGroups.has(group.id);
          const readyToMerge = needsMerge(group);
          const remarkChanged = hasRemarkChanged(group.id);

          return (
            <Box key={group.id} mb={3}>
              <Grid container alignItems="center" mb={1}>
                <Grid item xs>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      ...FONT_STYLE,
                    }}
                  >
                    {group.name}
                    {hasMergeResult && (
                      <FaCheck
                        color={"#1d62bf"}
                        style={{ fontSize: 20, paddingLeft: "4px" }}
                      />
                    )}
                    {mergeMode === MergeModeEnum.MergeResult &&
                      (() => {
                        const groupRemarks = allRemarks
                          .filter(
                            (ar) =>
                              ar.gradingDetailsGroupID === group.id &&
                              ar.remark?.trim() &&
                              ar.teamMemberID == null,
                          )
                          .map((ar) => ar.remark!.trim());
                        const hasGroupRemarks = groupRemarks.length > 0;
                        const groupRemarksText = groupRemarks.join("\n");
                        return (
                          <Grid
                            item
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mr: 1,
                              ml: 1,
                            }}
                          >
                            <Tooltip
                              title={
                                hasGroupRemarks ? (
                                  <Typography
                                    sx={{
                                      ...FONT_STYLE,
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    {groupRemarksText}
                                  </Typography>
                                ) : (
                                  ""
                                )
                              }
                              placement="top"
                              disableHoverListener={!hasGroupRemarks}
                              arrow
                              slotProps={{
                                popper: {
                                  modifiers: [
                                    {
                                      name: "offset",
                                      options: { offset: [14, -9] },
                                    },
                                  ],
                                },
                              }}
                            >
                              <span
                                style={{ cursor: "default", display: "flex" }}
                              >
                                {hasGroupRemarks && (
                                  <FaCommentDots
                                    color="#1d62bf"
                                    style={{ fontSize: 16 }}
                                  />
                                )}
                              </span>
                            </Tooltip>
                          </Grid>
                        );
                      })()}
                  </Typography>
                </Grid>

                <div
                  style={{
                    width: "150px",
                    display: "flex",
                    justifyContent: "space-between",
                    margin: "4px 14px",
                  }}
                >
                  {members.map((m) => {
                    const remark = allRemarks.find(
                      (ar) =>
                        ar.gradingDetailsGroupID === group.id &&
                        m?.id === ar.teamMemberID,
                    );
                    const hasRemark = !!remark?.remark?.trim();

                    return (
                      <div key={m!.id}>
                        <div
                          style={{
                            width: 60,
                            display: "flex",
                            justifyContent: "end",
                            alignItems: "end",
                            position: "relative",
                            cursor: "default",
                          }}
                        >
                          <Tooltip
                            title={
                              hasRemark ? (
                                <Typography
                                  sx={{
                                    ...FONT_STYLE,
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {remark!.remark}
                                </Typography>
                              ) : (
                                ""
                              )
                            }
                            placement="top"
                            disableHoverListener={!hasRemark}
                            arrow
                            slotProps={{
                              popper: {
                                modifiers: [
                                  {
                                    name: "offset",
                                    options: { offset: [16, -9] },
                                  },
                                ],
                              },
                            }}
                          >
                            <FaCommentDots
                              style={{
                                fontSize: 16,
                                paddingLeft: "4px",
                                color: "#1d62bf",
                                display: hasRemark ? "inline-block" : "none",
                              }}
                            />
                          </Tooltip>
                        </div>

                        <div
                          style={{
                            width: 60,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            position: "relative",
                          }}
                          title={m!.fullName}
                        >
                          <img
                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                              m!.fullName,
                            )}&background=random`}
                            alt={m!.fullName}
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Grid
                  item
                  sx={{
                    width: 90,
                    textAlign: "center",
                    ml: 2,
                    marginBottom: "4px",
                    marginTop: "4px",
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={FONT_STYLE}
                  >
                    Merge result
                  </Typography>
                </Grid>
              </Grid>

              {group.gradingDetails
                .slice()
                .sort((a, b) => a.index - b.index)
                .map((d) => {
                  const all = byDetail[d.id] ?? [];
                  const memberResults = all.filter((r) => r.teamMember);
                  const merged = all.find((r) => !r.teamMember);
                  const status = getStatus(memberResults, members.length);
                  return (
                    <Grid
                      container
                      alignItems="center"
                      sx={{
                        py: 0.5,
                        px: 0.5,
                        borderRadius: 1,
                        backgroundColor:
                          status === "Conflict"
                            ? "#fdecea"
                            : status === "Incomplete"
                              ? "#fff4e5"
                              : "transparent",
                      }}
                    >
                      <Grid
                        item
                        sx={{
                          width: 38,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {(() => {
                          const Icon = statusConfig[status]?.icon;
                          return (
                            <>
                              {Icon && (
                                <Icon
                                  size={17}
                                  style={{ color: statusConfig[status].color }}
                                />
                              )}
                            </>
                          );
                        })()}
                      </Grid>

                      <Grid item sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            ...FONT_STYLE,
                            display: "flex",
                            alignContent: "center",
                            marginBottom: "-1px",
                          }}
                        >
                          {d.text}
                        </Typography>
                      </Grid>

                      {members.map((m) => {
                        const r = memberResults.find(
                          (x) => x.teamMember?.id === m!.id,
                        );

                        return (
                          <Grid
                            item
                            key={m!.id}
                            sx={{ width: 80, textAlign: "center", ml: 1 }}
                          >
                            <TextField
                              size="small"
                              disabled={!r}
                              value={
                                editingKey === `${d.id}-${m!.id}`
                                  ? editValue
                                  : (r?.points?.toFixed(2) ?? "")
                              }
                              onFocus={() => {
                                if (!r) return;
                                setEditingKey(`${d.id}-${m!.id}`);
                                setEditValue(r.points.toFixed(2).toString());
                              }}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={() => r && commitEdit(r, d, m!, group.id)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && r) {
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              inputProps={{ style: FONT_STYLE }}
                            />
                          </Grid>
                        );
                      })}

                      <Grid item sx={{ width: 90, ml: 2 }}>
                        <TextField
                          size="small"
                          value={
                            status === "Complete" &&
                            mergeMode === MergeModeEnum.Merge
                              ? (memberResults
                                  .find(
                                    (x) => x.teamMember?.id === members[0]!.id,
                                  )
                                  ?.points?.toFixed(2) ?? "")
                              : (merged?.points?.toFixed(2) ?? "")
                          }
                          disabled
                          inputProps={{ style: FONT_STYLE }}
                        />
                      </Grid>
                    </Grid>
                  );
                })}

              <Grid container alignItems="center" mt={1}>
                <Grid item xs>
                  <Typography variant="body2" fontWeight={600} sx={FONT_STYLE}>
                    Total points
                    {group.averageFactor !== 1
                      ? ` (sum * ${group.averageFactor})`
                      : ""}
                  </Typography>
                </Grid>

                {totalsByGroup[gi].members.map((t, i) => (
                  <Grid
                    item
                    key={i}
                    sx={{ width: 80, textAlign: "center", ml: 1 }}
                  >
                    <Typography variant="body2" sx={FONT_STYLE}>
                      {t.toFixed(2)}
                    </Typography>
                  </Grid>
                ))}

                <Grid item sx={{ width: 90, textAlign: "center", ml: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={FONT_STYLE}>
                    {totalsByGroup[gi].merged.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>

              <Box
                sx={{ mt: 1, mr: 1 }}
                hidden={mergeMode === MergeModeEnum.MergeResult}
              >
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  placeholder="Remarks..."
                  value={remarksByGroup[group.id] ?? ""}
                  onChange={(e) =>
                    setRemarksByGroup((prev) => ({
                      ...prev,
                      [group.id]: e.target.value,
                    }))
                  }
                  inputProps={{
                    style: {
                      ...FONT_STYLE,
                      resize: "vertical",
                    },
                  }}
                />
              </Box>

              <Box
                sx={{ textAlign: "right", mt: 1 }}
                hidden={mergeMode === MergeModeEnum.MergeResult}
              >
                <Button
                  size="small"
                  disabled={
                    conflict ||
                    (!hasBeenModified && !readyToMerge && !remarkChanged) ||
                    (savedGroups.has(group.id) &&
                      !hasBeenModified &&
                      !remarkChanged)
                  }
                  variant="contained"
                  onClick={() => handleSaveGroup(group.id)}
                  sx={{ textTransform: "none", ...FONT_STYLE }}
                >
                  Save
                </Button>
              </Box>

              {gi !== groups.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          );
        })}
    </Box>
  );
};

export default MergePanel;
